import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Mail, MessageCircle, Zap, Shield, Code, ArrowRight, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const quickLinks = [
    { name: 'Inicio', path: '/' },
    { name: 'Nosotros', path: '/nosotros' },
    { name: 'APIs SUNAT', path: '/apis' },
    { name: 'Blog', path: '/blog' },
    { name: 'Galería', path: '/galeria' },
    { name: 'Documentación', path: '/documentacion' },
    { name: 'Contacto', path: '/contacto' }
  ];

  const apiServices = [
    'Consulta RUC/DNI',
    'Facturación Electrónica',
    'Notas Crédito/Débito',
    'Buzón Electrónico',
    'Tipo de Cambio',
    'Descarga de Archivos'
  ];

  const socialLinks = [
    { 
      icon: Facebook, 
      href: 'https://web.facebook.com/profile.php?id=100076234360325', 
      color: 'hover:bg-blue-600',
      label: 'Facebook'
    },
    { 
      icon: Globe, // Icono para página web
      href: 'https://visioner7.net',
      color: 'hover:bg-blue-700',
      label: 'Sitio Web'
    },
    { 
      icon: MessageCircle, // Icono para TikTok (puedes cambiar si encuentras uno mejor)
      href: 'https://www.tiktok.com/@visioner7', 
      color: 'hover:bg-pink-500',
      label: 'TikTok'
    },
    { 
      icon: Mail, 
      href: 'mailto:visioner7.com@gmail.com', 
      color: 'hover:bg-red-500',
      label: 'Email'
    },
    { 
      icon: MessageCircle, // Icono para WhatsApp
      href: 'https://wa.me/955000321', 
      color: 'hover:bg-green-500',
      label: 'WhatsApp'
    }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-blue-900 text-white relative overflow-hidden">
      {/* Efectos de fondo */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[linear-gradient(#fff_1px,transparent_1px),linear-gradient(90deg,#fff_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>
        
        {/* Partículas animadas */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-500/30 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * 200,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Sección principal */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8 lg:gap-12">
            {/* Branding */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-4"
              >
                Visioner7 APIs
              </motion.div>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Soluciones tecnológicas avanzadas para integración SUNAT. 
                APIs confiables para el éxito de tu negocio.
              </p>
              
              {/* Stats */}
              <div className="flex space-x-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">99.9%</div>
                  <div className="text-gray-400 text-sm">Uptime</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">24/7</div>
                  <div className="text-gray-400 text-sm">Soporte</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-cyan-400">API</div>
                  <div className="text-gray-400 text-sm">REST & SOAP</div>
                </div>
              </div>
            </motion.div>

            {/* Enlaces rápidos */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold mb-6 flex items-center">
                <Zap className="w-5 h-5 text-cyan-400 mr-2" />
                Navegación
              </h3>
              <div className="space-y-3">
                {quickLinks.map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={link.path}
                      className="group flex items-center text-gray-300 hover:text-white transition-all duration-300"
                    >
                      <ArrowRight className="w-3 h-3 mr-2 transform group-hover:translate-x-1 transition-transform" />
                      <span className="text-base">{link.name}</span>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* APIs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold mb-6 flex items-center">
                <Code className="w-5 h-5 text-cyan-400 mr-2" />
                Nuestras APIs
              </h3>
              <div className="space-y-3">
                {apiServices.map((service, index) => (
                  <motion.div
                    key={service}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-center text-gray-300 group cursor-pointer"
                  >
                    <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full mr-3 group-hover:scale-150 transition-transform" />
                    <span className="text-base group-hover:text-white transition-colors">{service}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Contacto y redes */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-semibold mb-6 flex items-center">
                <Shield className="w-5 h-5 text-cyan-400 mr-2" />
                Conecta con Nosotros
              </h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-center text-gray-300">
                  <Mail className="w-4 h-4 text-cyan-400 mr-3" />
                  <span>dev@visioner7.com</span>
                </div>
                <div className="flex items-center text-gray-300">
                  <MessageCircle className="w-4 h-4 text-cyan-400 mr-3" />
                  <span>+51 955 000 321</span>
                </div>
              </div>

              <div className="space-y-4">
                <p className="text-gray-400 text-sm">Síguenos en redes</p>
                <div className="flex space-x-3">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        className={`w-10 h-10 bg-gray-700/50 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-300 ${social.color} border border-gray-600 hover:border-transparent`}
                      >
                        <Icon className="w-4 h-4" />
                      </motion.a>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Línea divisoria */}
        <div className="border-t border-gray-700/50 relative">
          <div className="absolute left-1/2 top-0 w-32 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent transform -translate-x-1/2 -translate-y-1/2" />
        </div>

        {/* Bottom section */}
        <div className="py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center md:text-left"
            >
              <p className="text-gray-400 text-sm">
                © 2025 Visioner7 APIs. Todos los derechos reservados.
              </p>
              <p className="text-gray-500 text-xs mt-1">
                Desarrollado con ❤️ para la comunidad tecnológica peruana
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="flex items-center space-x-6 text-gray-400 text-sm"
            >
              <a href="#" className="hover:text-cyan-400 transition-colors">Términos</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Privacidad</a>
              <a href="#" className="hover:text-cyan-400 transition-colors">Cookies</a>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs">Sistema Online</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Efecto de gradiente inferior */}
      <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-cyan-900/20 to-transparent pointer-events-none" />
    </footer>
  );
};

export default Footer;